<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from '@axios'
import moment from 'moment-timezone'

const props = defineProps({
  userId: {
    type: String,
    required: true,
  },

  // Optional initial snapshot of the user's memberCommitment / override
  // subdocs. If omitted, the panel fetches its own data via the existing
  // GET /user/:id endpoint.
  initialCommitment: {
    type: Object,
    default: null,
  },
  initialOverride: {
    type: Object,
    default: null,
  },

  // Optional pre-loaded feedback list. If not provided, the panel fetches
  // its own cross-restaurant history for the member when used in admin
  // (platform admin) context.
  feedbackEntries: {
    type: Array,
    default: null,
  },
})

const commitment = ref(props.initialCommitment || null)
const override = ref(props.initialOverride || null)
const feedback = ref(props.feedbackEntries || [])
const loading = ref(false)
const submitting = ref(false)
const showOverrideForm = ref(false)
const overrideNote = ref('')
const errorMessage = ref(null)

const accepted = computed(() => Boolean(commitment.value?.accepted))

const acceptedAtDisplay = computed(() => {
  if (!commitment.value?.acceptedAt) return null
  
  return moment(commitment.value.acceptedAt).format('MMM D, YYYY h:mma')
})

const overrideAtDisplay = computed(() => {
  if (!override.value?.at) return null
  
  return moment(override.value.at).format('MMM D, YYYY h:mma')
})

const fetchStatus = async () => {
  if (commitment.value && override.value) return
  loading.value = true
  try {
    const res = await axios.get(`/user/${props.userId}`, {
      params: {},
    })
    commitment.value = res.data?.memberCommitment || { accepted: false }
    override.value = res.data?.memberCommitmentOverride || null
  } catch (err) {
    errorMessage.value = 'Failed to load commitment status'
  } finally {
    loading.value = false
  }
}

const fetchFeedback = async () => {
  if (props.feedbackEntries) return
  try {
    const res = await axios.get('/restaurant-admin/member-feedback', {
      params: {
        memberUserId: props.userId,
      },
    })
    feedback.value = res.data || []
  } catch (err) {
    // 403 expected for non-admins; silently leave feedback empty
    feedback.value = []
  }
}

const submitOverride = async () => {
  submitting.value = true
  errorMessage.value = null
  try {
    const res = await axios.post(
      `/user/${props.userId}/member-commitment/override`,
      { internalNote: overrideNote.value },
      { params: {} },
    )
    commitment.value = res.data.memberCommitment
    override.value = res.data.memberCommitmentOverride
    showOverrideForm.value = false
    overrideNote.value = ''
  } catch (err) {
    errorMessage.value = err.response?.data?.error || 'Override failed'
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  fetchStatus()
  fetchFeedback()
})
</script>

<template>
  <div>
    <div class="text-subtitle-2 mb-2 text-medium-emphasis">
      Member Commitment
    </div>

    <VAlert
      v-if="errorMessage"
      type="error"
      density="compact"
      class="mb-2"
    >
      {{ errorMessage }}
    </VAlert>

    <div
      v-if="loading"
      class="text-caption text-medium-emphasis"
    >
      Loading…
    </div>

    <template v-else>
      <div class="d-flex align-center gap-2 mb-2">
        <VChip
          :color="accepted ? 'success' : 'warning'"
          size="small"
          variant="tonal"
        >
          {{ accepted ? 'Accepted' : 'Not accepted' }}
        </VChip>
        <span
          v-if="accepted && commitment?.version"
          class="text-caption text-medium-emphasis"
        >
          v{{ commitment.version }}
        </span>
        <span
          v-if="accepted && commitment?.source"
          class="text-caption text-medium-emphasis"
        >
          · {{ commitment.source.replace(/_/g, ' ') }}
        </span>
        <span
          v-if="accepted && acceptedAtDisplay"
          class="text-caption text-medium-emphasis"
        >
          · {{ acceptedAtDisplay }}
        </span>
      </div>

      <div
        v-if="override?.byAdminUserId"
        class="text-caption text-medium-emphasis mb-2"
      >
        Admin override applied {{ overrideAtDisplay }}<span v-if="override?.note"> — “{{ override.note }}”</span>
      </div>

      <div v-if="!accepted && !showOverrideForm">
        <VBtn
          color="primary"
          variant="tonal"
          size="small"
          @click="showOverrideForm = true"
        >
          Mark as accepted (admin override)
        </VBtn>
      </div>

      <div
        v-if="showOverrideForm"
        class="mt-2"
      >
        <VTextarea
          v-model="overrideNote"
          label="Internal note (optional)"
          rows="2"
          density="compact"
          variant="outlined"
          hide-details
          class="mb-2"
        />
        <div class="d-flex gap-2">
          <VBtn
            color="primary"
            size="small"
            :loading="submitting"
            :disabled="submitting"
            @click="submitOverride"
          >
            Confirm override
          </VBtn>
          <VBtn
            variant="text"
            size="small"
            :disabled="submitting"
            @click="showOverrideForm = false; overrideNote = ''"
          >
            Cancel
          </VBtn>
        </div>
      </div>

      <!-- Phase 7: cross-restaurant feedback history -->
      <div
        v-if="feedback.length"
        class="mt-4"
      >
        <div class="text-caption font-weight-medium mb-1">
          Restaurant feedback history ({{ feedback.length }})
        </div>
        <VList
          density="compact"
          class="pa-0"
        >
          <VListItem
            v-for="entry in feedback"
            :key="entry._id"
            class="px-0"
          >
            <VListItemTitle class="text-body-2">
              <span class="font-weight-medium">{{ entry.restaurantName || entry.restaurantId }}</span>
              · {{ entry.category.replace(/_/g, ' ') }}
              <VChip
                v-if="entry.autoDeclineActive"
                color="error"
                size="x-small"
                variant="tonal"
                class="ml-1"
              >
                Auto-decline
              </VChip>
            </VListItemTitle>
            <VListItemSubtitle
              v-if="entry.internalNotes"
              class="text-caption"
            >
              {{ entry.internalNotes }}
            </VListItemSubtitle>
            <VListItemSubtitle class="text-caption text-medium-emphasis">
              {{ moment(entry.createdAt).format('MMM D, YYYY') }}
            </VListItemSubtitle>
          </VListItem>
        </VList>
      </div>
    </template>
  </div>
</template>
